/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-08 15:35:00
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-09-02 17:25:38
 */
import React, { useEffect, Dispatch } from 'react'
import { Table, Spin, TablePaginationConfig } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Key } from 'react'
import Viewer from '../../../components/ViewerV2'
import StatusIndicator from './components/StatusIndicator'
import PhotoWithTooltip from './components/PhotoWithTooltip'
import useTableResizer from './hooks/useTableResizer'
import usePhotoViewer from './hooks/usePhotoViewer'
import { RectifyRecord, RectifyDataState, PaginationState } from '../../types'

// Props 类型
interface RectifyTableProps {
  data: RectifyDataState
  loading: boolean
  pagination: PaginationState
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters?: any,
    sorter?: any
  ) => void
  selectedRowKeys: Key[]
  setSelectedRowKeys: (keys: Key[]) => void
  myRole: number | null
  userId: string | number | null
  handleDelete: (record: RectifyRecord) => void
  handleShare: (record: RectifyRecord) => void
  dispatch?: Dispatch<any>
}

/**
 * 整改记录表格组件
 */
const RectifyTable: React.FC<RectifyTableProps> = (props) => {
  const {
    data,
    loading,
    pagination,
    handleTableChange,
    selectedRowKeys,
    setSelectedRowKeys,
    myRole,
    userId,
    handleDelete,
    handleShare,
    dispatch
  } = props

  // 使用自定义Hook管理表格大小
  const tableHeight = useTableResizer(380)

  // 使用自定义Hook管理照片预览
  const {
    viewerRef,
    viewerPhotos,
    activeTooltip,
    setActiveTooltip,
    handlePhotoPreview
  } = usePhotoViewer(dispatch)

  // 格式化时间，2024年8月之前的置灰
  const renderTimeWithStyle = (timeStr: string | null | undefined) => {
    if (!timeStr) return '-'

    const time = new Date(timeStr)
    const threshold = new Date('2024-08-01')
    const isGray = time < threshold

    return <span style={{ color: isGray ? '#999' : 'inherit' }}>{timeStr}</span>
  }

  // 数据变化时重新计算表格布局
  useEffect(() => {
    // 当数据加载完成后，延迟执行以确保表格渲染完成
    if (!loading && data.records.length > 0) {
      setTimeout(() => {
        // 触发窗口resize事件以强制表格重新计算布局
        window.dispatchEvent(new Event('resize'))
      }, 200)
    }
  }, [data, loading])

  // 定义表格列
  const columns: ColumnsType<RectifyRecord> = [
    {
      title: '编号',
      dataIndex: 'recordNo',
      key: 'recordNo',
      width: 100
    },
    {
      title: '发起人',
      dataIndex: 'createUserName',
      key: 'createUserName',
      width: 100
    },
    {
      title: '发起时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (time: string) => renderTimeWithStyle(time)
    },
    {
      title: '任务描述',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      render: (text: string) => (text ? text : '-')
    },
    {
      title: '发起照片',
      dataIndex: 'photoFilesBefore',
      key: 'photoFilesBefore',
      width: 160,
      render: (photos: string[], record: RectifyRecord) => (
        <PhotoWithTooltip
          photos={photos}
          type="整改前"
          record={record}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handlePhotoPreview={handlePhotoPreview}
        />
      )
    },
    {
      title: '完成照片',
      dataIndex: 'photoFilesAfter',
      key: 'photoFilesAfter',
      width: 160,
      render: (photos: string[], record: RectifyRecord) => (
        <PhotoWithTooltip
          photos={photos}
          type="整改后"
          record={record}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handlePhotoPreview={handlePhotoPreview}
        />
      )
    },
    {
      title: '任务状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => <StatusIndicator status={status} />
    },
    {
      title: '待办人',
      dataIndex: 'rectificationUser',
      key: 'rectificationUser',
      width: 120,
      render: (users: Array<{ userName: string }>) => {
        if (!users || users.length === 0) return '-'
        return users.map((user) => user.userName).join(', ')
      }
    },
    {
      title: '完成人',
      dataIndex: 'updateUserName',
      key: 'updateUserName',
      width: 120,
      render: (text: string, record: RectifyRecord) =>
        record.status === 2 && text ? text : '-'
    },
    {
      title: '任务截止时间',
      dataIndex: 'deadline',
      key: 'deadline',
      width: 180,
      render: (time: string) => renderTimeWithStyle(time)
    },
    {
      title: '任务完成时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 180,
      render: (time: string, record: RectifyRecord) =>
        renderTimeWithStyle(record.status === 2 ? time : '')
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: RectifyRecord) => {
        // 判断删除权限
        const canDelete =
          myRole === 1 || myRole === 2 || record.createUserID === userId
        return (
          <div className="action-buttons">
            <a
              className="action-link"
              style={{ color: '#1890ff', marginRight: 16 }}
              onClick={() => handleShare(record)}
            >
              分享
            </a>
            <a
              className={`action-link ${!canDelete ? 'disabled-link' : ''}`}
              style={{ color: canDelete ? '#0070F4' : '#999999' }}
              onClick={() => canDelete && handleDelete(record)}
            >
              删除
            </a>
          </div>
        )
      }
    }
  ]

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: Key[]) => setSelectedRowKeys(keys),
    fixed: true as const
  }

  // 计算列宽总和
  const totalColumnsWidth = columns.reduce(
    (total, col) => total + (typeof col.width === 'number' ? col.width : 120),
    0
  )

  return (
    <>
      <Spin spinning={loading}>
        <div
          className="rectify-table-container"
          style={{
            position: 'relative',
            width: 'calc(100vw - 220px)',
            overflow: 'hidden'
          }}
        >
          <Table
            columns={columns}
            dataSource={data.records}
            rowKey="recordID"
            rowSelection={rowSelection}
            tableLayout="fixed"
            scroll={{
              x: totalColumnsWidth,
              y: tableHeight,
              scrollToFirstRowOnChange: true
            }}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            style={{ width: '100%' }}
          />
        </div>
      </Spin>

      {/* 使用ViewerV2组件 */}
      <Viewer
        ref={viewerRef as any}
        photos={viewerPhotos}
        showWatermarkAction={false}
        isShowComments={false}
        fromPlace={'rectifyRecords'}
        canDelete={false}
        canRectify={false}
        canAiSecurity={false}
      />
    </>
  )
}

export default RectifyTable
